import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import editIcon from 'assets/images/icons/edit.svg';
import fieldCollapseGreyIcon from 'assets/images/icons/field-collapse-grey.svg';
import messageSquareGreyIcon from 'assets/images/icons/message-square-grey.svg';
import AlertModal from 'components/common-components/AlertModal';
import Loader from 'components/common-components/Loader';
import MediaPreviewModal from './MediaPreviewModal';
import ReportActions from './ReportActions';
import sectionToggleHook from './sectionToggleHook';
import { CallAPI } from 'actions/General';
import elementCommentIconHook from './elementCommentIconHook';

const ReportModal = ({
  webReportInfo,
  setWebReportInfo,
  editManagerSection,
  commentCallback,
  elementCommentCallback,
  isNestedModal,
}) => {
  const [loader, setLoader] = useState(false);
  const [displayedHtml, setDisplayedHtml] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [previewId, setPreviewId] = useState('');

  const reportContainerRef = useRef(null);

  // Custom hooks
  sectionToggleHook(displayedHtml);

  const closeModal = () => setWebReportInfo(null);

  const getViewDetail = async (previewId, dataType, actionType) => {
    let result = await CallAPI(
      'GET_PREVIEW_DETAIL',
      { previewId, dataOf: dataType, action: actionType },
      null,
      setLoader,
      null,
      null,
    );
    if (result.status) {
      result = await CallAPI(
        'EXTERNAL_POST_API',
        { ...result.data.data },
        result.data.url,
        setLoader,
        null,
        null,
      );
      setDisplayedHtml(result.data.html);
    } else {
      setErrorMessage(result.error);
    }
  };

  const getReportPreviewId = async () => {
    const payload = {
      previewOf: 'submission',
      formId: webReportInfo.formId,
      submissionId: webReportInfo.submissionId,
    };
    const result = await CallAPI('GET_PREVIEW_ID', payload, null, setLoader);
    if (result.status) {
      const dataType = webReportInfo.dataType;
      getViewDetail(result.data._id, dataType, 'web_link');
      setPreviewId(result.data._id);
    }
  };

  const clickEditAction = () => {
    if (webReportInfo.row) editManagerSection(webReportInfo.row);
  };

  const clickCommentAction = () => {
    if (webReportInfo.row) commentCallback(webReportInfo.row);
  };

  const handleSubmissionElementCommentClick = async (elementGlobalId, sectionRepetitionNumber) => {
    if (webReportInfo.row)
      elementCommentCallback(webReportInfo.row, elementGlobalId, sectionRepetitionNumber);
  };

  // Custom hooks
  elementCommentIconHook(
    displayedHtml,
    reportContainerRef,
    webReportInfo,
    handleSubmissionElementCommentClick,
  );

  useEffect(() => {
    if (webReportInfo) {
      getReportPreviewId();
    }
  }, []);

  return (
    <AlertModal
      show={!!webReportInfo}
      actionButtons={[]}
      noPadding
      size='xl'
      backdropClassName={isNestedModal ? 'backdrop-overlap' : ''}
    >
      <div className='w-100'>
        {loader && <Loader color='green-1' type='text-center my-3' />}
        {displayedHtml && (
          <>
            <div
              className='theme-background-white-5 py-2 d-flex justify-content-center align-items-center gap-2 cursor-pointer rounded-top-md'
              onClick={() => closeModal()}
            >
              <img src={fieldCollapseGreyIcon} />
              <label className='cursor-pointer'>Close</label>
            </div>
            <div
              style={{
                maxHeight: '80vh',
                overflow: 'auto',
                position: 'relative',
                marginBottom: '2.5rem',
              }}
            >
              <div ref={reportContainerRef} dangerouslySetInnerHTML={{ __html: displayedHtml }} />
              <ReportActions
                dataType={webReportInfo.dataType}
                previewId={previewId}
                htmlContent={displayedHtml}
              />
            </div>
            <div className='submission-report-modal-actions-icons'>
              <div className='d-flex gap-2'>
                {webReportInfo.allowEditManagerSection && (
                  <div
                    className='theme-background-green-1 p-3 rounded-circle cursor-pointer'
                    onClick={() => clickEditAction()}
                  >
                    <img src={editIcon} />
                  </div>
                )}
                <div
                  className='theme-background-green-1 p-3 rounded-circle cursor-pointer'
                  onClick={() => clickCommentAction()}
                >
                  <img className='svg-white' src={messageSquareGreyIcon} />
                </div>
              </div>
            </div>
            <MediaPreviewModal displayedHtml={displayedHtml} />
          </>
        )}
        {errorMessage && (
          <AlertModal
            show={true}
            title='Invalid Link'
            message="Link Doesn't Exist"
            actionButtons={[]}
          />
        )}
      </div>
    </AlertModal>
  );
};

ReportModal.propTypes = {
  webReportInfo: PropTypes.object,
  setWebReportInfo: PropTypes.func.isRequired,
  editManagerSection: PropTypes.func.isRequired,
  commentCallback: PropTypes.func.isRequired,
  elementCommentCallback: PropTypes.func.isRequired,
  isNestedModal: PropTypes.bool,
};

export default ReportModal;
